import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

import uniqueId from 'lodash/uniqueId';
import { useSearchParams } from 'react-router';

import type { HumanMessage, Message } from '@/features/ai-chat/types/chat.types';
import { clock } from '@/services/clock';

interface Options {
  conversationId: string | null;
  messagesRef: RefObject<Message[]>;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  setAnimatedHumanId: Dispatch<SetStateAction<string | null>>;
  setAnimatedText: Dispatch<SetStateAction<string | null>>;
}

export function useUrlPromptIntegration({
  conversationId,
  messagesRef,
  setMessages,
  setAnimatedHumanId,
  setAnimatedText
}: Options) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) return;
    const prompt = searchParams.get('prompt');
    if (!prompt || !conversationId) return;

    hasProcessedRef.current = true;

    setMessages(prev => {
      const now = clock.date().toISOString();
      const human: HumanMessage = {
        id: uniqueId(now),
        sender: 'human',
        text: prompt,
        timestamp: now
      };
      const next = [...prev, human];
      messagesRef.current = next;
      setAnimatedHumanId(human.id);
      setAnimatedText(prompt);
      return next;
    });

    setSearchParams(
      prev => {
        prev.delete('prompt');
        return prev;
      },
      { replace: true }
    );
  }, [
    conversationId,
    searchParams,
    setSearchParams,
    messagesRef,
    setMessages,
    setAnimatedHumanId,
    setAnimatedText
  ]);
}
